import { z } from 'zod'

// the performance page looks backwards over executed orders, so a range is always explicit.
// a year is the practical ceiling: the aggregation replays every instrument's full fill ledger
// and the response zero-fills one row per calendar day.
const MAX_PERFORMANCE_WINDOW_MS = 366 * 24 * 60 * 60 * 1000

// shared range validation for both performance queries: ordered and bounded.
const refineRange = (d: { from: Date; to: Date }, ctx: z.RefinementCtx): void => {
  if (d.from.getTime() > d.to.getTime()) {
    ctx.addIssue({ code: 'custom', message: '`from` must be <= `to`', path: ['from'] })
  }
  if (d.to.getTime() - d.from.getTime() > MAX_PERFORMANCE_WINDOW_MS) {
    ctx.addIssue({
      code: 'custom',
      message: `range must be <= ${MAX_PERFORMANCE_WINDOW_MS / (24 * 3_600_000)}d`,
      path: ['to'],
    })
  }
}

export const dayTradePerformanceDailyQuerySchema = z
  .object({
    from: z.coerce.date(),
    to: z.coerce.date(),
    // optional narrowing to a single tracked instrument; omitted means every instrument
    // of the account
    instrumentUid: z.string().min(1).optional(),
  })
  .superRefine(refineRange)

export type DayTradePerformanceDailyQueryDto = z.infer<typeof dayTradePerformanceDailyQuerySchema>

// one calendar day of bot activity. the day boundary is 00:00 UTC — the same boundary the engine
// resets its daily counters on — so this page cannot disagree with the bot's own stats.
export const dayTradePerformanceDaySchema = z.object({
  // the UTC day, as YYYY-MM-DD
  date: z.string(),
  // executed buy orders that day
  buys: z.number().int(),
  // executed sell orders that day
  sells: z.number().int(),
  // FIFO realized P&L of that day's sells, BEFORE tax: gross − commission. deliberately pre-tax —
  // the tax base is pooled across instruments and across a whole period (see `totals.tax`), so
  // taxing a single day in isolation would overstate it on any day that sits beside a losing one.
  realizedBeforeTax: z.number(),
})

export type DayTradePerformanceDayDto = z.infer<typeof dayTradePerformanceDaySchema>

export const dayTradePerformanceDailyResponseSchema = z.object({
  // every calendar day in the requested range, zero-filled, oldest first — a quiet stretch is
  // as much a part of the distribution as a busy one
  days: z.array(dayTradePerformanceDaySchema),
  // range-level figures. the tax is pooled over the whole range exactly as the account stats card
  // pools it across instruments — losses in the range shrink the base the gains are taxed out of —
  // so `netRealized` here is directly comparable to the card, while the per-day series above is not.
  totals: z.object({
    buys: z.number().int(),
    sells: z.number().int(),
    // Σ of the days' pre-tax figures
    realizedBeforeTax: z.number(),
    // max(0, Σ(FIFO gain − round-trip commission)) × rate, computed once over the whole range
    tax: z.number(),
    // realizedBeforeTax − tax
    netRealized: z.number(),
    // the rate the tax was computed at, so the UI can label it
    taxRatePercent: z.number(),
  }),
})

export type DayTradePerformanceDailyResponseDto = z.infer<
  typeof dayTradePerformanceDailyResponseSchema
>

export const dayTradePerformanceOrdersQuerySchema = z
  .object({
    instrumentUid: z.string().min(1),
    from: z.coerce.date(),
    to: z.coerce.date(),
  })
  .superRefine(refineRange)

export type DayTradePerformanceOrdersQueryDto = z.infer<typeof dayTradePerformanceOrdersQuerySchema>

// one executed order, ready to be drawn on the price chart. the sell-side fields come from the
// FIFO replay of the instrument's COMPLETE fill history, so a sell whose matching buy predates
// the requested range still reports the right cost basis and hold duration.
export const dayTradePerformanceOrderSchema = z.object({
  orderId: z.string(),
  side: z.enum(['BUY', 'SELL']),
  timestamp: z.coerce.date(),
  // executed per-share price
  price: z.number(),
  lots: z.number().int(),
  // per-share average price of the FIFO buy lots this sell closed; null for buys and for a sell
  // that closed nothing
  matchedAvgPrice: z.number().nullable(),
  // when the OLDEST closed lot was bought (timestamp − heldMs); null as above. with more than one
  // matched lot this is the start of the longest hold, not the buy `matchedAvgPrice` belongs to —
  // `matchedLotCount` tells the UI when to say so.
  matchedBuyAt: z.coerce.date().nullable(),
  // how many distinct FIFO buy lots this sell closed; 0 for buys and non-closing sells
  matchedLotCount: z.number().int(),
  // longest matched-lot hold duration in ms; null as above
  heldMs: z.number().nullable(),
  // FIFO-matched realized gross (sell proceeds − cost of the closed lots); null as above
  realizedGross: z.number().nullable(),
  // gross − pro-rated round-trip commission − per-sell tax; null as above
  realizedNet: z.number().nullable(),
  // the trade's per-share return: (price − matchedAvgPrice) / matchedAvgPrice × 100. gross, so it
  // pairs with realizedGross; null as above
  profitPercent: z.number().nullable(),
})

export type DayTradePerformanceOrderDto = z.infer<typeof dayTradePerformanceOrderSchema>

export const dayTradePerformanceOrdersResponseSchema = z.object({
  // executed orders inside the range, oldest first
  orders: z.array(dayTradePerformanceOrderSchema),
})

export type DayTradePerformanceOrdersResponseDto = z.infer<
  typeof dayTradePerformanceOrdersResponseSchema
>
